import React, { useState, useEffect } from 'react';
import { HiOutlineBell, HiOutlineExclamation, HiCheck } from 'react-icons/hi';
import { useAuth } from '../context/AuthContext';
import notificationService from '../services/notificationService';
import './NotificationSettings.css';

const NotificationSettings = () => {
  const { userId } = useAuth();
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    checkNotificationStatus();
    
    // Disabled auto-prompt - users can enable from profile settings
    // const timer = setTimeout(() => {
    //   if (permission !== 'granted' && !localStorage.getItem('notification-prompt-dismissed')) {
    //     setShowPrompt(true);
    //   }
    // }, 10000);

    // return () => clearTimeout(timer);
  }, [permission]);

  const checkNotificationStatus = async () => {
    if (!notificationService.isSupported()) {
      return;
    }

    const perm = notificationService.getPermission();
    setPermission(perm);

    if (perm === 'granted') {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    }
  };

  const handleEnable = async () => {
    setLoading(true);
    try {
      const perm = await notificationService.requestPermission();
      setPermission(perm);

      if (perm === 'granted') {
        await notificationService.subscribe(userId);
        setIsSubscribed(true);
        setShowPrompt(false);
        
        // Show test notification
        setTimeout(() => {
          notificationService.showTestNotification();
        }, 500);
      }
    } catch (error) {
      console.error('Failed to enable notifications:', error);
      alert('Failed to enable notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      await notificationService.unsubscribe(userId);
      setIsSubscribed(false);
    } catch (error) {
      console.error('Failed to disable notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismissPrompt = () => {
    localStorage.setItem('notification-prompt-dismissed', 'true');
    setShowPrompt(false);
  };

  if (!notificationService.isSupported()) {
    return null;
  }

  // Floating prompt - DISABLED
  // Users can enable notifications from profile settings if needed
  if (false && showPrompt && permission !== 'granted') {
    return (
      <div className="notification-prompt">
        <div className="notification-prompt-content">
          <div className="notification-prompt-icon" aria-hidden="true"><HiOutlineBell /></div>
          <div className="notification-prompt-text">
            <strong>Get notified of new messages</strong>
            <p>Enable notifications to never miss a message</p>
          </div>
        </div>
        <div className="notification-prompt-actions">
          <button className="notification-prompt-enable" onClick={handleEnable} disabled={loading}>
            {loading ? 'Enabling...' : 'Enable'}
          </button>
          <button className="notification-prompt-dismiss" onClick={handleDismissPrompt}>
            ×
          </button>
        </div>
      </div>
    );
  }

  // Settings toggle (for Profile page)
  return (
    <div className="notification-settings">
      <div className="notification-settings-header">
        <div className="notification-settings-icon" aria-hidden="true"><HiOutlineBell /></div>
        <div className="notification-settings-info">
          <h4>Push Notifications</h4>
          <p>Get notified when you receive new messages</p>
        </div>
      </div>

      {permission === 'denied' && (
        <div className="notification-blocked">
          <p style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <HiOutlineExclamation aria-hidden="true" style={{ flexShrink: 0 }} /> Notifications are blocked. Please enable them in your browser settings.
          </p>
        </div>
      )}

      {permission === 'default' && (
        <button 
          className="notification-enable-btn" 
          onClick={handleEnable}
          disabled={loading}
        >
          {loading ? 'Enabling...' : 'Enable Notifications'}
        </button>
      )}

      {permission === 'granted' && (
        <div className="notification-status">
          {isSubscribed ? (
            <>
              <div className="notification-enabled" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <HiCheck aria-hidden="true" /> Notifications enabled
              </div>
              <button 
                className="notification-disable-btn" 
                onClick={handleDisable}
                disabled={loading}
              >
                {loading ? 'Disabling...' : 'Disable'}
              </button>
            </>
          ) : (
            <button 
              className="notification-enable-btn" 
              onClick={handleEnable}
              disabled={loading}
            >
              {loading ? 'Enabling...' : 'Re-enable Notifications'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
